/**
 * When a money request got NO answer.
 *
 * The request left, and then the connection dropped (Wi-Fi, a mobile network
 * switching, a proxy or the browser giving up). axios then rejects with no
 * `error.response`. That does NOT mean nothing happened: the server keeps
 * going when the browser leaves, and live tests showed it finishing the
 * payment every time (pipeline audit F1). So the honest answer is "we don't
 * know yet", never "nothing was charged".
 *
 * Every money request here sends an idempotency key and keeps it on failure,
 * so pressing the same button again with the same details gets the first
 * answer back instead of a second charge. The messages say so.
 */

/** True when the request was sent but no answer came back. */
export function noAnswer(error) {
  if (!error || error.response) return false;
  return Boolean(error.request)
    || error.code === "ERR_NETWORK"
    || error.code === "ECONNABORTED"
    || error.message === "Network Error";
}
/**
 * True when the answer did not come because Sourship took too long (the
 * `timeout` from config/timeouts.js ran out), rather than because the
 * connection dropped.
 *
 * axios rejects a timeout with "timeout of 30000ms exceeded" (code
 * ECONNABORTED, or ETIMEDOUT with clarifyTimeoutError). A cancelled request is
 * ECONNABORTED too, but says "Request aborted": that is not a timeout.
 */
export function timedOut(error) {
  if (!error || error.response) return false;
  return error.code === "ETIMEDOUT" || /timeout/i.test(error.message || "");
}

/* The two ways a sentence can start: the connection dropped, or Sourship was
   slow to answer. The rest of each sentence is the same either way. */
export const LOST = "We lost the connection before we heard back";
export const SLOW = "Sourship is taking longer than usual to answer";

const ENDINGS = {
  payment: ", so we don't know if the payment went through. Check your wallet history, or press Pay again: it won't charge you twice.",
  importRequests: ", so your requests may have been sent. Check Sourcing requests before sending them again. Sending the same file again won't make them twice.",
  importOrders: ", so your orders may have been imported and charged. Check your orders before importing again. Importing the same file again won't charge you twice.",
  sourcingRequest: ", so this request may have been sent. Check Sourcing requests and your wallet before sending it again. Confirming the same request again won't charge you twice.",
  credits: ", so the credits may have been bought. Check your wallet history before buying again. Buying the same pack again in the next few minutes won't charge you twice.",
  order: ", so this order may have been created and charged. Check your orders before creating it again. Pressing Pay again for the same order won't charge you twice.",
  heldOrders: ", so these orders may have been processed and charged. Check your orders before processing them again. Processing the same orders again won't charge you twice.",
  topUp: ", so your top-up request may have been sent. Check your wallet history before sending it again. Sending the same receipt again won't make a second request.",
  shippingRequest: ", so this request may have been sent. Check your requests before sending it again. Sending the same request again won't make a second one.",
};

/**
 * The message for one kind of action (see ENDINGS).
 *
 * Pass the axios `error` so a time limit reads as Sourship being slow rather
 * than as a dropped connection. Without it the connection wording is kept.
 */
export function unknownOutcome(kind, error) {
  const lead = timedOut(error) ? SLOW : LOST;
  return lead + (ENDINGS[kind] || ENDINGS.payment);
}
